import { PanelRightClose, PanelRightOpen } from "lucide-react";
import { forwardRef, useId } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/v2/sidebar";
import { TourBeacon } from "@/features/tours/components/TourBeacon";
import { useAuth } from "@/lib/hooks/useAuth";
import { cn } from "@/lib/utils/cn";
import useAdminSidebarRoutes from "@/routes/useAdminSidebarRoutes";
import { useClientSidebarRoutes } from "@/routes/useClientSidebarRoutes";

export const ClientSidebar = forwardRef<HTMLDivElement>((_props, ref) => {
  const { t } = useTranslation();
  const { context } = useAuth();
  const { state } = useSidebar();
  const location = useLocation();
  const adminRoutes = useAdminSidebarRoutes();
  const clientRoutes = useClientSidebarRoutes();
  const { toggleSidebar } = useSidebar();
  const beaconDescriptionId = useId();
  const { topRoutes, bottomRoutes } =
    context === "admin" ? adminRoutes : clientRoutes;

  return (
    <Sidebar
      ref={ref}
      collapsible="icon"
      className={cn(
        "overflow-hidden",
        context === "admin"
          ? "mt-28 h-[calc(100vh-7rem)]"
          : "mt-16 h-[calc(100vh-4rem)]"
      )}
    >
      <SidebarContent className="pt-4">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {topRoutes.map((item) => (
                <SidebarMenuItem key={item.label}>
                  <SidebarMenuButton
                    asChild
                    tooltip={item.label}
                    isActive={location.pathname.startsWith(
                      item.path.replace(/\/\d{4}\/\d{1,2}$/, "")
                    )}
                  >
                    <Link
                      to={item.path}
                      aria-describedby={item.beacon ? `${beaconDescriptionId}-${item.path}` : undefined}
                    >
                      <item.icon />
                      <span>{item.label}</span>
                    </Link>
                  </SidebarMenuButton>
                  {item.beacon && (
                    <>
                      <TourBeacon className="absolute right-3 top-1/2 -translate-y-1/2 group-data-[collapsible=icon]:right-2 group-data-[collapsible=icon]:top-2 group-data-[collapsible=icon]:translate-y-0" />
                      <span id={`${beaconDescriptionId}-${item.path}`} className="sr-only">
                        {item.beacon}
                      </span>
                    </>
                  )}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {bottomRoutes.map((item) => (
                <SidebarMenuItem key={item.label}>
                  <SidebarMenuButton
                    asChild
                    tooltip={item.label}
                    isActive={location.pathname.startsWith(
                      item.path.replace(/\/\d{4}\/\d{1,2}$/, "")
                    )}
                  >
                    <Link to={item.path}>
                      <item.icon />
                      <span>{item.label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip={
                    state === "expanded"
                      ? t("common.collapseSidebar")
                      : t("common.expandSidebar")
                  }
                  onClick={toggleSidebar}
                  asChild
                >
                  <button className="cursor-pointer">
                    {state === "expanded" ? (
                      <PanelRightOpen />
                    ) : (
                      <PanelRightClose />
                    )}
                    <span>{t("common.collapseSidebar")}</span>
                  </button>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarFooter>
    </Sidebar>
  );
});

ClientSidebar.displayName = "ClientSidebar";
